/**
 * The corner report: your template is being made, and then that it is ready.
 *
 * It occupies the spot the agent dock had, which is why that is hidden — two
 * floating launchers in one corner is "drag one of them".
 *
 * Positioned through `DashboardLayout`'s `overlay` prop rather than fixed to
 * the viewport, for the reason that prop exists: **the phone tab bar is a
 * flex sibling of `<main>`, not an overlay**, so anything fixed at
 * `bottom: 1rem` sits on top of "More" and covers a navigation control.
 * Inside `<main>`, "the bottom" means the bottom of the content area.
 *
 * It draws nothing when there is nothing to report — not a dot, not a shell.
 * A permanent badge saying "no runs" is chrome reporting the normal case.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@monorepo/common/components/ui';
import { landedTitle, useTemplateRunsContext } from './TemplateRunsContext.jsx';
import { askToNotify, notifyState } from './notify.js';

/** What a RUNNING job is doing, by kind. */
const GOING = {
  generate: () => 'Making a template',
  edit: (j) => `Editing ${j.template?.name || 'a template'}`,
  render: (j) => `Exporting ${j.template?.name || 'a design'}`,
  video: (j) => `Rendering ${j.template?.name || 'a video'}`,
};

/** Where tapping a landed run goes, and what the button says. */
export function landedAction(job) {
  const file = job.outcome?.file;
  if ((job.kind === 'render' || job.kind === 'video') && file?.id) {
    return { to: `/media?file=${file.id}`, label: 'View in library' };
  }
  if (job.outcome?.question) {
    // A generation's question is answered in the composer, which still has
    // the run; an edit's in the chat of its template.
    return { to: job.template?.id ? `/media/templates?template=${job.template.id}&chat=1` : `/media/templates?job=${job.id}`, label: 'Answer' };
  }
  // `?job=` rather than `?template=`: a generation whose page was left has
  // no template row yet — its result is still inside the job — and the
  // studio knows how to turn either into an open editor.
  const to = job.template?.id ? `/media/templates?template=${job.template.id}` : `/media/templates?job=${job.id}`;
  return { to, label: job.status === 'done' ? 'Open' : 'See why' };
}

export default function RunBubble({ hideOn = [] }) {
  const runs = useTemplateRunsContext();
  const navigate = useNavigate();
  const [asked, setAsked] = React.useState(false);
  // `useLocation`, not `window.location.pathname`, because the path is a
  // render INPUT and this component is not the one that owns it.
  const { pathname: path } = useLocation();

  if (!runs?.allowed) return null;
  // Hidden where it would be in the way: the immersive editors fill the
  // screen and have their own bottom bar, and the templates page shows these
  // runs itself — the editor reports its own chat and exports in place.
  if (hideOn.some((p) => path === p || path.startsWith(`${p}/`))) return null;

  const going = runs.active[0];
  const landed = runs.landed[0];
  if (!going && !landed) return null;
  const more = runs.active.length + runs.landed.length - 1;

  // Asked on a TAP and only while something is running: the permission is
  // one-shot and cannot be re-asked from the page, so prompting before
  // anybody has started anything spends it on nothing.
  const canAsk = !!going && !asked && notifyState() === 'default';
  const action = landed ? landedAction(landed) : null;
  const tone = going ? 'animate-pulse bg-accent'
    : landed.outcome?.question ? 'bg-warning'
      : landed.status === 'done' ? 'bg-success' : 'bg-danger';

  const open = () => {
    runs.dismiss(landed.id);
    navigate(action.to);
  };

  return (
    <div className="pointer-events-auto absolute bottom-3 right-3 z-30 max-w-[min(22rem,calc(100%-1.5rem))]"
         data-run-bubble="">
      <div className="flex items-start gap-3 rounded-card border border-line bg-surface-raised p-3 shadow-lg">
        <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${tone}`} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm text-ink" data-run-bubble-title="">
            {going ? (GOING[going.kind] || GOING.generate)(going) : landedTitle(landed)}
          </p>
          <p className="truncate text-caption text-ink-muted" data-run-bubble-detail="">
            {going
              ? (going.progress?.text || going.summary || 'Working…')
              : (landed.outcome?.question || landed.template?.name || landed.outcome?.file?.filename || landed.summary || '')}
          </p>
          {more > 0 && <p className="text-caption-sm text-ink-subtle" data-run-bubble-more="">+{more} more</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {landed && (
              <Button size="sm" onClick={open} data-run-bubble-open="">{action.label}</Button>
            )}
            {canAsk && (
              <Button size="sm" variant="secondary" data-run-bubble-notify=""
                      onClick={async () => { setAsked(true); await askToNotify(); }}>
                Tell me when it is done
              </Button>
            )}
            {landed && (
              <Button size="sm" variant="ghost" onClick={() => runs.dismiss(landed.id)} data-run-bubble-dismiss="">
                Dismiss
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
